/**
 * Offline Write Queue
 *
 * Provides a persistent queue for critical writes that:
 * - Stores pending writes in IndexedDB (survives page refresh/app restart)
 * - Automatically flushes when connectivity returns
 * - Retries with exponential backoff on transient failures
 * - Uses idempotency keys to prevent duplicate writes
 * - Emits events for UI updates (pending count, sync status)
 *
 * Critical write types:
 * - Baby tracking events (feeding, diaper, sleep, etc.)
 * - Check-ins
 * - Task completions
 * - Calendar events
 */

import { addDoc, setDoc, updateDoc, doc, collection, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { addBreadcrumb, captureException } from './errorTracking';

// =============== Types ===============

export type WriteOperation = 'add' | 'set' | 'update' | 'callable';

export interface QueuedWrite {
  /** Unique ID for this queued write */
  id: string;
  /** Idempotency key to prevent duplicate writes */
  idempotencyKey: string;
  /** Firestore collection path (for Firestore ops) or function name (for callable) */
  collectionPath: string;
  /** Document ID (optional for 'add' operations) */
  docId?: string;
  /** The data to write / callable function input */
  data: Record<string, unknown>;
  /** Type of write operation */
  operation: WriteOperation;
  /** When this was queued */
  queuedAt: number;
  /** Number of retry attempts */
  retryCount: number;
  /** Last error message if any */
  lastError?: string;
  /** When to retry next (timestamp) */
  nextRetryAt?: number;
  /** Category for grouping in UI */
  category: 'babyTracking' | 'checkin' | 'task' | 'calendar' | 'other';
  /** Human-readable description for UI */
  description?: string;
}

export interface QueueStats {
  pendingCount: number;
  oldestQueuedAt: number | null;
  categories: Record<string, number>;
}

// =============== IndexedDB Setup ===============

const DB_NAME = 'bloom_offline_queue';
const DB_VERSION = 1;
const STORE_NAME = 'pending_writes';
const PROCESSED_STORE = 'processed_keys';

let dbInstance: IDBDatabase | null = null;
let dbInitPromise: Promise<IDBDatabase | null> | null = null;

/**
 * Get or initialize the IndexedDB database
 */
async function getDb(): Promise<IDBDatabase | null> {
  if (dbInstance) return dbInstance;
  if (dbInitPromise) return dbInitPromise;

  dbInitPromise = new Promise((resolve) => {
    // Check if IndexedDB is available (not in Safari private mode)
    if (typeof indexedDB === 'undefined') {
      console.warn('[OfflineQueue] IndexedDB not available');
      resolve(null);
      return;
    }

    try {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.warn('[OfflineQueue] Failed to open database:', request.error);
        resolve(null);
      };

      request.onsuccess = () => {
        dbInstance = request.result;
        resolve(dbInstance);
      };

      request.onupgradeneeded = (event) => {
        const database = (event.target as IDBOpenDBRequest).result;

        // Store for pending writes
        if (!database.objectStoreNames.contains(STORE_NAME)) {
          const store = database.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('idempotencyKey', 'idempotencyKey', { unique: true });
          store.createIndex('queuedAt', 'queuedAt', { unique: false });
          store.createIndex('category', 'category', { unique: false });
        }

        // Store for processed idempotency keys (to prevent re-processing)
        if (!database.objectStoreNames.contains(PROCESSED_STORE)) {
          const processedStore = database.createObjectStore(PROCESSED_STORE, { keyPath: 'key' });
          processedStore.createIndex('processedAt', 'processedAt', { unique: false });
        }
      };
    } catch (err) {
      console.warn('[OfflineQueue] Error initializing database:', err);
      resolve(null);
    }
  });

  return dbInitPromise;
}

// =============== Event Emitter ===============

type QueueEventType = 'change' | 'flush-start' | 'flush-complete' | 'write-success' | 'write-error';
type QueueEventListener = (data?: unknown) => void;

const listeners = new Map<QueueEventType, Set<QueueEventListener>>();

export function onQueueEvent(event: QueueEventType, listener: QueueEventListener): () => void {
  if (!listeners.has(event)) {
    listeners.set(event, new Set());
  }
  listeners.get(event)!.add(listener);
  return () => listeners.get(event)?.delete(listener);
}

function emitQueueEvent(event: QueueEventType, data?: unknown): void {
  listeners.get(event)?.forEach((listener) => listener(data));
}

// =============== Queue Operations ===============

/**
 * Generate a unique ID for queue entries
 */
function generateQueueId(): string {
  return `q_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Generate an idempotency key from write parameters
 * Uses collection + doc + timestamp window to ensure uniqueness
 */
export function generateIdempotencyKey(
  collectionPath: string,
  docId: string | undefined,
  category: QueuedWrite['category'],
  timestamp?: Date
): string {
  const ts = timestamp || new Date();
  // Round to 5-second window for near-duplicate detection
  const timeWindow = Math.floor(ts.getTime() / 5000);
  const docPart = docId || 'new';
  return `${category}_${collectionPath}_${docPart}_${timeWindow}`;
}

/**
 * Check if an idempotency key was already processed
 */
async function isProcessed(idempotencyKey: string): Promise<boolean> {
  const database = await getDb();
  if (!database) return false;

  return new Promise((resolve) => {
    const tx = database.transaction(PROCESSED_STORE, 'readonly');
    const store = tx.objectStore(PROCESSED_STORE);
    const request = store.get(idempotencyKey);
    request.onsuccess = () => resolve(!!request.result);
    request.onerror = () => resolve(false);
  });
}

/**
 * Mark an idempotency key as processed
 */
async function markProcessed(idempotencyKey: string): Promise<void> {
  const database = await getDb();
  if (!database) return;

  return new Promise((resolve, reject) => {
    const tx = database.transaction(PROCESSED_STORE, 'readwrite');
    const store = tx.objectStore(PROCESSED_STORE);
    store.put({ key: idempotencyKey, processedAt: Date.now() });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Add a write to the offline queue
 */
export async function queueWrite(
  write: Omit<QueuedWrite, 'id' | 'queuedAt' | 'retryCount'>
): Promise<string | null> {
  const database = await getDb();
  if (!database) {
    console.warn('[OfflineQueue] Cannot queue write - IndexedDB not available');
    return null;
  }

  // Check if already processed (duplicate)
  if (await isProcessed(write.idempotencyKey)) {
    console.log('[OfflineQueue] Write already processed, skipping:', write.idempotencyKey);
    return null;
  }

  const queuedWrite: QueuedWrite = {
    ...write,
    id: generateQueueId(),
    queuedAt: Date.now(),
    retryCount: 0,
  };

  return new Promise((resolve, reject) => {
    const tx = database.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.add(queuedWrite);
    tx.oncomplete = async () => {
      addBreadcrumb({ category: 'offline_queue', message: 'Write queued', data: { category: write.category } });
      emitQueueEvent('change');

      // Register for background sync to ensure queue gets flushed
      // This is done async and we don't wait for it
      registerBackgroundSyncIfNeeded();

      resolve(queuedWrite.id);
    };
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Register background sync if supported (called after queueing a write)
 */
async function registerBackgroundSyncIfNeeded(): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    if ('sync' in registration) {
      await (registration.sync as { register: (tag: string) => Promise<void> }).register('sync-offline-queue');
    }
  } catch {
    // Silently fail - background sync is a progressive enhancement
  }
}

/**
 * Get all pending writes from the queue
 */
export async function getPendingWrites(): Promise<QueuedWrite[]> {
  const database = await getDb();
  if (!database) return [];

  return new Promise((resolve) => {
    const tx = database.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => resolve([]);
  });
}

/**
 * Get queue statistics for UI display
 */
export async function getQueueStats(): Promise<QueueStats> {
  const writes = await getPendingWrites();
  const categories: Record<string, number> = {};

  for (const write of writes) {
    categories[write.category] = (categories[write.category] || 0) + 1;
  }

  return {
    pendingCount: writes.length,
    oldestQueuedAt: writes.length > 0 ? Math.min(...writes.map((w) => w.queuedAt)) : null,
    categories,
  };
}

/**
 * Remove a write from the queue
 */
async function removeFromQueue(id: string): Promise<void> {
  const database = await getDb();
  if (!database) return;

  return new Promise((resolve, reject) => {
    const tx = database.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.delete(id);
    tx.oncomplete = () => {
      emitQueueEvent('change');
      resolve();
    };
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Update a queued write (for retry tracking)
 */
async function updateQueuedWrite(write: QueuedWrite): Promise<void> {
  const database = await getDb();
  if (!database) return;

  return new Promise((resolve, reject) => {
    const tx = database.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.put(write);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}



// =============== Write Processing ===============

const MAX_RETRIES = 5;
const BASE_RETRY_DELAY_MS = 1000;

/**
 * Check if an error is retryable (transient network error)
 */
function isRetryableError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const err = error as { code?: string; message?: string };

  // Network errors
  if (err.code === 'unavailable' || err.code === 'resource-exhausted') return true;
  if (err.message?.includes('network') || err.message?.includes('offline')) return true;
  if (err.message?.includes('Failed to fetch')) return true;

  return false;
}

/**
 * Process a single queued write - execute the Firestore or callable operation
 */
async function processWrite(write: QueuedWrite): Promise<boolean> {
  try {
    // Convert any serialized Timestamps back to Firestore Timestamps
    const data = { ...write.data };
    for (const [key, value] of Object.entries(data)) {
      if (value && typeof value === 'object' && '_seconds' in value && '_nanoseconds' in value) {
        const v = value as { _seconds: number; _nanoseconds: number };
        data[key] = new Timestamp(v._seconds, v._nanoseconds);
      }
    }

    switch (write.operation) {
      case 'add': {
        const colRef = collection(db, write.collectionPath);
        await addDoc(colRef, data);
        break;
      }
      case 'set':
        if (!write.docId) throw new Error('docId required for set operation');
        await setDoc(doc(db, write.collectionPath, write.docId), data);
        break;
      case 'update':
        if (!write.docId) throw new Error('docId required for update operation');
        await updateDoc(doc(db, write.collectionPath, write.docId), data);
        break;
      case 'callable': {
        // For callable functions, collectionPath contains the function name
        const callableFn = httpsCallable(functions, write.collectionPath);
        await callableFn(data);
        break;
      }
    }

    // Mark as processed to prevent duplicate writes
    await markProcessed(write.idempotencyKey);
    await removeFromQueue(write.id);

    emitQueueEvent('write-success', { category: write.category, description: write.description });
    addBreadcrumb({ category: 'offline_queue', message: 'Write synced', data: { category: write.category } });

    return true;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);

    if (isRetryableError(error) && write.retryCount < MAX_RETRIES) {
      // Schedule retry with exponential backoff
      const delay = BASE_RETRY_DELAY_MS * Math.pow(2, write.retryCount);
      write.retryCount++;
      write.lastError = errorMessage;
      write.nextRetryAt = Date.now() + delay;
      await updateQueuedWrite(write);

      console.log(`[OfflineQueue] Retry ${write.retryCount}/${MAX_RETRIES} scheduled in ${delay}ms`);
      return false;
    }

    // Non-retryable error or max retries exceeded
    emitQueueEvent('write-error', {
      category: write.category,
      error: errorMessage,
      description: write.description
    });
    captureException(error instanceof Error ? error : new Error(errorMessage), {
      extra: { queuedWrite: write },
    });

    // Remove from queue to prevent infinite retry loop
    await removeFromQueue(write.id);
    return false;
  }
}

// =============== Queue Flushing ===============

let isFlushingQueue = false;

/**
 * Flush all pending writes from the queue
 * Returns counts of successful and failed writes
 */
export async function flushQueue(): Promise<{ success: number; failed: number }> {
  if (isFlushingQueue) {
    console.log('[OfflineQueue] Flush already in progress');
    return { success: 0, failed: 0 };
  }

  // Check if online before attempting flush
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    console.log('[OfflineQueue] Cannot flush - offline');
    return { success: 0, failed: 0 };
  }

  const writes = await getPendingWrites();
  if (writes.length === 0) {
    return { success: 0, failed: 0 };
  }

  isFlushingQueue = true;
  emitQueueEvent('flush-start');
  addBreadcrumb({ category: 'offline_queue', message: 'Flush started', data: { count: writes.length } });

  let success = 0;
  let failed = 0;

  // Sort by queuedAt to process oldest first
  writes.sort((a, b) => a.queuedAt - b.queuedAt);

  for (const write of writes) {
    // Skip if scheduled for later retry
    if (write.nextRetryAt && write.nextRetryAt > Date.now()) {
      continue;
    }

    const result = await processWrite(write);
    if (result) {
      success++;
    } else {
      failed++;
    }
  }

  isFlushingQueue = false;
  emitQueueEvent('flush-complete', { success, failed });
  addBreadcrumb({ category: 'offline_queue', message: 'Flush complete', data: { success, failed } });

  return { success, failed };
}

/**
 * Check if queue flush is in progress
 */
export function isFlushInProgress(): boolean {
  return isFlushingQueue;
}

// =============== Cleanup ===============

/**
 * Clean up old processed keys to prevent IndexedDB bloat
 */
export async function cleanupProcessedKeys(olderThanDays: number = 7): Promise<number> {
  const database = await getDb();
  if (!database) return 0;

  const cutoff = Date.now() - olderThanDays * 24 * 60 * 60 * 1000;

  return new Promise((resolve) => {
    const tx = database.transaction(PROCESSED_STORE, 'readwrite');
    const store = tx.objectStore(PROCESSED_STORE);
    const index = store.index('processedAt');
    const range = IDBKeyRange.upperBound(cutoff);

    let deletedCount = 0;
    const request = index.openCursor(range);

    request.onsuccess = () => {
      const cursor = request.result;
      if (cursor) {
        cursor.delete();
        deletedCount++;
        cursor.continue();
      }
    };

    tx.oncomplete = () => {
      if (deletedCount > 0) {
        console.log(`[OfflineQueue] Cleaned up ${deletedCount} old processed keys`);
      }
      resolve(deletedCount);
    };
    tx.onerror = () => resolve(0);
  });
}

// =============== Background Sync Registration ===============

/**
 * Register for background sync to ensure queue is flushed when connectivity returns
 * This is more reliable than 'online' event, especially on mobile/iOS
 */
async function registerBackgroundSync(): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    // Check if sync is supported (not available on iOS Safari PWA)
    if ('sync' in registration) {
      await (registration.sync as { register: (tag: string) => Promise<void> }).register('sync-offline-queue');
      console.log('[OfflineQueue] Background sync registered');
    }
  } catch (err) {
    // Background sync not supported or failed - fall back to online event
    console.log('[OfflineQueue] Background sync not available:', err);
  }
}

/**
 * Listen for messages from the service worker
 */
function setupServiceWorkerListener(): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }

  navigator.serviceWorker.addEventListener('message', (event) => {
    const { type } = event.data || {};

    if (type === 'FLUSH_OFFLINE_QUEUE') {
      console.log('[OfflineQueue] Received flush request from service worker');
      flushQueue().catch(console.error);
    }
  });
}

// =============== Auto-flush on Connectivity ===============

if (typeof window !== 'undefined') {
  // Set up service worker message listener
  setupServiceWorkerListener();

  // Flush queue when coming back online
  window.addEventListener('online', () => {
    console.log('[OfflineQueue] Network online - flushing queue');
    flushQueue().catch(console.error);
  });

  // Also attempt flush on visibility change (tab becomes visible)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && navigator.onLine) {
      flushQueue().catch(console.error);
    }
  });

  // Run cleanup periodically (on app start, then every 24 hours)
  cleanupProcessedKeys().catch(console.error);
  setInterval(() => cleanupProcessedKeys().catch(console.error), 24 * 60 * 60 * 1000);
}

// Export for manual registration if needed
export { registerBackgroundSync };

// =============== Callable Function Queue Helper ===============

/**
 * Queue a callable function for later execution when offline
 * @param functionName - The Firebase callable function name
 * @param data - The input data for the function
 * @param category - Category for UI grouping
 * @param description - Human-readable description
 * @returns The queue entry ID or null if queueing failed
 */
export async function queueCallable(
  functionName: string,
  data: Record<string, unknown>,
  category: QueuedWrite['category'],
  description?: string
): Promise<string | null> {
  const idempotencyKey = generateIdempotencyKey(
    functionName,
    undefined,
    category,
    new Date()
  );

  return queueWrite({
    idempotencyKey,
    collectionPath: functionName, // For callable, this is the function name
    data,
    operation: 'callable',
    category,
    description,
  });
}
